/**
 * Resend Welcome Email Modal — Pick a client and resend the post-verify
 * welcome email. Reuses sendPostVerifyWelcome's admin testToEmail path,
 * which bypasses the postVerifyEmailSent idempotency flag.
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

interface ClientOption {
  id: string;
  name: string;
  email: string;
}

interface ResendWelcomeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  clients: ClientOption[];
}

export function ResendWelcomeEmailModal({ isOpen, onClose, clients }: ResendWelcomeEmailModalProps) {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedClientId('');
      setResult(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedClient = clients.find(c => c.id === selectedClientId) || null;

  const handleClose = () => {
    setSelectedClientId('');
    setResult(null);
    onClose();
  };

  const handleSend = async () => {
    if (!selectedClient) return;
    setSending(true);
    setResult(null);
    try {
      const fn = httpsCallable(functions, 'sendPostVerifyWelcome');
      await fn({
        testToEmail: selectedClient.email,
        testName: selectedClient.name || 'Client',
      });
      setResult({
        success: true,
        message: `Welcome email resent to ${selectedClient.email}.`,
      });
    } catch (err) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSending(false);
    }
  };

  const sortedClients = [...clients].sort((a, b) =>
    (a.name || a.email).localeCompare(b.name || b.email),
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={handleClose}>
      <div style={{
        background: 'var(--dark-2)', borderRadius: '12px', padding: '32px',
        maxWidth: '480px', width: '90%', border: '1px solid var(--dark-3)',
        maxHeight: '90vh', overflowY: 'auto',
      }} onClick={e => e.stopPropagation()}>

        <h2 style={{ margin: '0 0 4px', color: 'var(--white)' }}>Resend Welcome Email</h2>
        <p style={{ color: 'var(--gray-400)', fontSize: '0.85rem', marginBottom: '20px' }}>
          Select a client to resend the post-verification welcome email and Client User Guide PDF.
        </p>

        <div className="form-group" style={{ marginBottom: '12px' }}>
          <label style={{ color: 'var(--gray-300)', fontSize: '0.85rem' }}>Client</label>
          <select
            value={selectedClientId}
            onChange={e => setSelectedClientId(e.target.value)}
            disabled={sending}
            style={{
              width: '100%', padding: '10px', background: 'var(--dark)',
              border: '1px solid var(--dark-3)', borderRadius: '6px',
              color: 'var(--white)', fontFamily: 'inherit', fontSize: '0.9rem',
            }}
          >
            <option value="">Select a client...</option>
            {sortedClients.map(c => (
              <option key={c.id} value={c.id}>
                {c.name || '(no name)'} — {c.email}
              </option>
            ))}
          </select>
        </div>

        {selectedClient && (
          <div style={{
            padding: '10px 12px', borderRadius: '6px', marginBottom: '16px',
            background: 'rgba(200,168,78,0.08)', border: '1px solid rgba(200,168,78,0.3)',
            color: 'var(--gray-200)', fontSize: '0.8rem', lineHeight: 1.5,
          }}>
            Sending to: <strong>{selectedClient.email}</strong><br />
            Greeting: Hi <strong>{(selectedClient.name || 'there').split(/\s+/)[0]}</strong>,
          </div>
        )}

        <button
          onClick={handleSend}
          disabled={sending || !selectedClient}
          className="btn btn-primary"
          style={{ width: '100%', marginBottom: '10px', padding: '12px' }}
        >
          {sending ? 'Sending...' : 'Resend Welcome Email'}
        </button>

        {result && (
          <div style={{
            padding: '12px 14px', borderRadius: '8px', marginBottom: '12px',
            background: result.success ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
            border: `1px solid ${result.success ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
            color: result.success ? '#10b981' : '#dc2626',
            fontSize: '0.85rem', textAlign: 'center',
          }}>
            {result.message}
          </div>
        )}

        <button onClick={handleClose} className="btn btn-secondary" style={{ width: '100%' }}>
          {result?.success ? 'Done' : 'Cancel'}
        </button>
      </div>
    </div>
  );
}
